import type { SettingElementInterface } from '@/types/items'
import type { DetailStat } from '@/types/browser'
import type {
    SwadeEdgeInterface,
    SwadeHindranceInterface,
    SwadeSkillInterface,
    SwadeItemInterface,
} from '@/types/items'
import { useFormatModifier } from '@/composables/useFormatModifier'

export function edgeTypeLabel(
    el: SettingElementInterface,
    labels: Record<string, string>,
): string {
    const type = (el as SwadeEdgeInterface).type
    return labels[type ?? ''] ?? type ?? ''
}

export function hindranceSeverityLabel(
    el: SettingElementInterface,
    labels: Record<string, string>,
): string {
    const type = (el as SwadeHindranceInterface).type
    if (type === 'major') return labels.major ?? 'Major'
    if (type === 'minor') return labels.minor ?? 'Minor'
    if (type === 'choice') return labels.choice ?? 'Major or Minor'
    return ''
}

export function skillAttributeLabel(
    el: SettingElementInterface,
    labels: Record<string, string>,
): string {
    const attribute = (el as SwadeSkillInterface).linkedAttribute
    return labels[attribute ?? ''] ?? attribute ?? ''
}

export function itemTypeLabel(
    el: SettingElementInterface,
    labels: Record<string, string>,
): string {
    const type = (el as SwadeItemInterface).type
    return labels[type ?? ''] ?? type ?? ''
}

const { formatModifier } = useFormatModifier()

export function itemDetailSummary(
    el: SettingElementInterface,
    labels: Record<string, string>,
): DetailStat[] {
    const details = (el as SwadeItemInterface).details
    if (!details) return []

    const stats: DetailStat[] = []

    // Falsy values are skipped, matching the `v-if="details?.x"` gates the browser
    // detail pane already uses, so a zero AP or ROF does not occupy a slot.
    const push = (label: string, value: unknown) => {
        if (!value) return
        stats.push({ label, value: String(value) })
    }

    switch ((el as SwadeItemInterface).type) {
        case 'weapon':
            push(labels.damage ?? 'Damage', details.damage)
            push(labels.range ?? 'Range', details.range)
            push(labels.rateOfFire ?? 'ROF', details.rateOfFire)
            push(labels.ap ?? 'AP', details.ap)
            break
        case 'armor':
            if (details.armor) {
                push(
                    labels.armorValue ?? 'Armor',
                    details.toughness
                        ? `${formatModifier(details.armor)} / ${formatModifier(details.toughness)}`
                        : formatModifier(details.armor),
                )
            }
            push(labels.minStr ?? 'Min Str', details.minStr)
            break
        case 'shield':
            if (details.parry) push(labels.parryValue ?? 'Parry', formatModifier(details.parry))
            if (details.cover) push(labels.coverBonus ?? 'Cover', formatModifier(details.cover))
            push(labels.minStr ?? 'Min Str', details.minStr)
            break
        case 'cyberware':
            // Strain and installs always show, matching the browser detail pane: a zero
            // strain is meaningful, and a null install limit means unlimited.
            stats.push({ label: labels.strain ?? 'Strain', value: String(details.strain ?? 0) })
            stats.push({ label: labels.multipleLimit ?? 'Installs', value: String(details.installLimit ?? '∞') })
            break
        default:
            push(labels.weight ?? 'Weight', details.weight)
    }

    return stats
}
